// Per-route content for the Learn More panel.
//
// Slide schema:
//
//   {
//     title:         string  — heading shown at the top of the panel body
//     paragraphs:    string[] — one paragraph per array entry, rendered
//                               back-to-back in the panel
//     learnMoreUrl:  string  — target of the "Learn more" link, opened in
//                               a new tab
//     videoUrl?:     string  — OPTIONAL. iframe embed URL for the video
//                               modal. When set, the play thumbnail and
//                               "Watch Academy Video" text link open the
//                               modal. When unset, both fall back to
//                               opening `learnMoreUrl` in a new tab so the
//                               button stays useful.
//     thumbnailUrl?: string  — OPTIONAL. static image URL used as the
//                               poster for the panel's video thumbnail
//                               area. When unset the thumbnail is a solid
//                               tinted tile with just the play icon on
//                               top.
//   }
//
// Order matters — more specific patterns first, then broader ones. The
// provider walks this list top-to-bottom against the current pathname
// (via `matchPath`) and picks the first hit, falling back to
// DEFAULT_SLIDE if nothing matches.
//
// The paragraphs are the exact text shown in the panel body. The
// learnMoreUrl points at a matching page on docs.layerd.com; the docs
// page starts with the same two paragraphs so the docs and the panel
// stay in sync when you edit them.

const DOCS_BASE = "https://docs.layerd.com";

const SLIDES_BY_PATH = [
  // -------- foundational pages ---------------------------------------
  {
    pattern: "/v2/overview",
    slide: {
      title: "About the Overview",
      paragraphs: [
        "The Overview is your account-wide security dashboard. It answers \"what happened on my traffic in the last N hours\" at a glance — total requests inbound and outbound, how they trended against the previous period, where in the world detections came from, and the top attack types the WAF is catching right now.",
        "Use the time-range picker in the top right to switch between the last 24 hours and a custom range. Click any row in the Top WAF Detections table to jump straight to the filtered event list for that attack type. Two panels split by severity — one for WAF detections, one for issues identified — help you triage without having to open every screen.",
      ],
      learnMoreUrl: `${DOCS_BASE}/overview`,
    },
  },
  {
    pattern: "/v2/sites/*",
    slide: {
      title: "About Sites",
      paragraphs: [
        "The Sites page lists every domain onboarded to your account, with the option to expand any row to see its subdomains. Two columns tell you at a glance whether a site is doing anything: Status reflects the site record itself (Active or Disabled), and Enforcement reflects the WAF mode currently applied to traffic (Enforced, Detection, Unhealthy, or Disabled).",
        "Use the filter bar to narrow by status or enforcement mode, and the search box to jump to a specific domain, origin, or project. Adding a domain launches a guided setup that walks you through DNS delegation, SSL, and the initial WAF configuration. Row actions let you edit a site, disable it temporarily, or delete it — deletion is soft by default so you can restore recently-removed sites.",
      ],
      learnMoreUrl: `${DOCS_BASE}/sites`,
    },
  },
  {
    pattern: "/v2/ssl-tls/*",
    slide: {
      title: "About SSL/TLS",
      paragraphs: [
        "The SSL/TLS section covers everything about how your sites are served over HTTPS: the certificate protecting each site, the TLS profile in use, minimum protocol version, HSTS, and redirects. The Overview view is account-wide — a single table across every site with an expiry timeline and posture score. The Configuration view is per-site — that's where you actually change things.",
        "Start with the Overview to spot certificates that expire in the next 30 days, sites still on legacy TLS 1.0/1.1, or issuers you weren't expecting. Then click into a site to install a new certificate (Universal, Amazon ACM, DigiCert, upload custom, or generate a Layerd Origin cert), pick an encryption mode, tune the TLS profile, and turn on HSTS.",
      ],
      learnMoreUrl: `${DOCS_BASE}/ssl-tls`,
    },
  },

  // -------- detection event pages ------------------------------------
  {
    pattern: "/v2/detection/waf_event/*",
    slide: {
      title: "About WAF Events",
      paragraphs: [
        "WAF Events is the audit log for every request the Web Application Firewall inspected — attack pattern matches, verdicts, actions taken, and enough context to trace a single request end-to-end. The table sits under a filter bar with common filters (IP, host, URI, status) and a \"More Filters\" button that opens the full set: event ID, source, country, method, response status, user agent, JA3 fingerprint, attack type, risk level, verdict, action, severity.",
        "The Overview strip at the top gives a period rollup — total detections, how many were blocked, how many were critical, and which attack type is currently the top offender. The heatmap below groups activity by hour of day and day of week so you can see whether the traffic pattern is human, automated, or campaign-shaped. Click any row to open the drawer with the full request, matched rule, and quick \"filter by this IP / this country / this attack type\" shortcuts.",
      ],
      learnMoreUrl: `${DOCS_BASE}/waf-events`,
    },
  },
  {
    pattern: "/v2/detection/firewall_event/*",
    slide: {
      title: "About Firewall Events",
      paragraphs: [
        "Firewall Events shows every request that matched one of your firewall rules. Where WAF Events records attack-pattern matches, Firewall Events records rule matches — the two views are complementary. Each row identifies the rule that fired, the match type (regex, equals, greater-than, and so on), the action taken (Blocked, Challenged, or Passed), and the request context.",
        "Use the filter bar to narrow by IP, host, URI, or status. The More Filters button opens the fuller set including Match Type, Block Reason, and Event ID. Click a row to open the drawer, and use the \"Filter similar\" callbacks in the drawer to quickly build a query around one IP, country, status, or detection type.",
      ],
      learnMoreUrl: `${DOCS_BASE}/firewall-events`,
    },
  },
  {
    pattern: "/v2/detection/bot_event/*",
    slide: {
      title: "About Bot Events",
      paragraphs: [
        "Bot Events is the request-level audit for the Bot Management engine — which requests were classified as bots, what type, and what action was taken. The event feed is filterable by IP, host, URI, status (Blocked / Challenged / Detect), country, method, response status, bot type, and bot name. An activity heatmap above the table groups fires by hour and day so campaign patterns are easy to spot.",
        "Two columns are worth understanding. Bot type is the top-level classification (good bot, bad bot, unverified, human) with a fallback showing a fingerprint hint when the type is unknown. Detectors shows which signals contributed to the verdict — you'll see up to four chips, with a \"+N\" tooltip listing the rest.",
      ],
      learnMoreUrl: `${DOCS_BASE}/bot-events`,
    },
  },
  {
    pattern: "/v2/detection/rate_limit_event/*",
    slide: {
      title: "About Rate Limit Events",
      paragraphs: [
        "Rate Limit Events records every request that either counted toward or tripped one of your rate-limit rules. Each row identifies the rule that matched, its configured limit and window (e.g. 100 requests per 60 seconds), the action taken (Log-only, Challenge, Block, or Drop), and the mode the rule was in when it fired (Enforce or Shadow).",
        "Filter by IP, host, URI, country, method, response status, JA3 fingerprint, or by the threshold value. Click any row to open the drawer for the full request context. This is the primary tool for confirming a rate-limit rule is behaving the way you expect after you draft it or tune its parameters — run it in Shadow mode, watch the events, then flip to Enforce when the pattern looks right.",
      ],
      learnMoreUrl: `${DOCS_BASE}/rate-limit-events`,
    },
  },

  // -------- findings -------------------------------------------------
  {
    pattern: "/v2/findings/posture",
    slide: {
      title: "About Posture Findings",
      paragraphs: [
        "Posture Findings is the vulnerability inventory for your APIs and web-facing endpoints — every issue found by the posture scanner, grouped by severity and resource. The table shows the finding title, the affected resource, the component (URI or endpoint) where it was seen, CWE ID, tags, and which project the resource belongs to.",
        "Click a finding to open the details drawer, where you can review each affected instance (URI, method, parameter, evidence, plus request and response snapshots), read the reference material, and mark the finding as Closed with an acceptance reason. Findings you've accepted don't disappear — they move to Closed status so you can audit what was decided later.",
      ],
      learnMoreUrl: `${DOCS_BASE}/api-security-posture`,
    },
  },
  {
    pattern: "/v2/findings/threat-insights",
    slide: {
      title: "About Threat Insights",
      paragraphs: [
        "Threat Insights turns raw traffic into named threats. Each row is a finding — a specific rule matched a specific subject (an IP, an identity, an API key, a session, or the fleet as a whole) with enough evidence to warrant attention. Findings are grouped under two tabs: Per-site, one per site, and Cross-site, rules that span every site you own.",
        "Use the filter bar to narrow by severity, status (Open, Acknowledged, Resolved, Suppressed), rule, or time range. Silence a finding to tell the rule to skip that subject for a chosen duration with a reason — the rule stops producing new findings for that scope until the suppression expires. The freshness chip next to the Cross-site tab tells you when the last cross-site finding fired, so a quiet tab doesn't hide a broken pipeline.",
      ],
      learnMoreUrl: `${DOCS_BASE}/threat-insights`,
    },
  },
  {
    pattern: "/v2/findings/*",
    slide: {
      title: "About Findings",
      paragraphs: [
        "Findings groups the two ways Layerd surfaces risks in your account: Posture Findings (vulnerabilities scanned across your endpoints) and Threat Insights (rule-driven detections against live traffic). Both are audit-trailed and support Accept / Suppress workflows.",
        "Pick the sub-page from the left rail. Posture is best when you want to know \"what's broken in my API surface\"; Threat Insights is best when you want to know \"who's actively attacking me right now.\"",
      ],
      learnMoreUrl: `${DOCS_BASE}/api-security-posture`,
    },
  },

  // -------- service configuration pages ------------------------------
  {
    pattern: "/v2/services/waf_configuration",
    slide: {
      title: "About WAF Configuration",
      paragraphs: [
        "WAF Configuration is the fleet view for the Web Application Firewall — how many of your sites have each protection module turned on, what mode they're running in, and where to change things. Two module families are surfaced: Core Protection (Layerd AI Smart Parse, our reasoning-based detection engine) and Additional Protection (Signature-based WAF, rule-driven detection for known attack patterns).",
        "Each card shows how many sites currently have the module enabled, the coverage percentage, and a Configure link that opens the settings drawer for a specific site. The drawer is where you toggle a module on or off, pick a sensitivity (Low / Medium / High for modules that support it), and set the action taken on a match (Detect for log-only, Block, or Challenge).",
      ],
      learnMoreUrl: `${DOCS_BASE}/waf-configuration`,
    },
  },
  {
    pattern: "/v2/services/web_application/*",
    slide: {
      title: "About WAF Rules & Exceptions",
      paragraphs: [
        "The WAF Rules screens are where you add site-specific detection rules and carve out exceptions to the built-in protection. Custom Rules lets you define your own patterns (regex-based or condition-composed) that the WAF should treat as detections; Exceptions lets you tell the WAF to skip specific requests that would otherwise trigger — legitimate traffic from a known vendor, a URL your team uses for testing, and so on.",
        "Each rule has a name, a set of match conditions (IP, path, method, headers, and more), an action (Detect, Block, Challenge, or Allow), and an enable toggle. Rules are ordered by priority; the first match wins. Exceptions are evaluated before other WAF logic — if a request matches an exception, the WAF steps aside for that request.",
      ],
      learnMoreUrl: `${DOCS_BASE}/waf-rules`,
    },
  },
  {
    pattern: "/v2/services/rate_limit_rules",
    slide: {
      title: "About Rate Limit Rules",
      paragraphs: [
        "Rate Limit Rules is the list of rate-limit policies defined for a site. Each rule watches a characteristic (source IP, IP + path, JA3 fingerprint, and so on) and takes action once traffic to that characteristic crosses a threshold within a time window. Rules can Block, Challenge, Drop, or Log-only, and each rule can run in Shadow mode while you're validating.",
        "Rules are ordered in the list; the first rule whose characteristic matches an incoming request will evaluate that request against its threshold. Use the Add Rule button to create a new one — the drawer walks through the characteristic, window (seconds), request threshold, action, and mitigation timeout. Before you flip a new rule to Enforce, ship it as Shadow and watch Rate Limit Events for a while.",
      ],
      learnMoreUrl: `${DOCS_BASE}/rate-limit-rules`,
    },
  },
  {
    pattern: "/v2/services/bot/*",
    slide: {
      title: "About Bot Management",
      paragraphs: [
        "Bot Management identifies and acts on non-human traffic. The Config screen is where you choose what the bot engine does site by site — turn it on, pick actions for good bots (Allow or Challenge) and bad bots (Block, Challenge, or Log-only), and decide how strict you want the unverified-traffic threshold to be. Actions apply immediately once you Apply the draft.",
        "Beyond actions, the sensitivity floors let you tune specific signals — how quickly the engine will treat a burst of same-URI requests as scraping, how many unique paths per second look like enumeration, how tightly to hold a session's rhythm. Most operators start with the Medium preset and only touch individual floors after seeing a specific bot pattern get through or after a legitimate customer is unfairly challenged.",
      ],
      learnMoreUrl: `${DOCS_BASE}/bot-management`,
    },
  },
  {
    pattern: "/v2/services/ddos*",
    slide: {
      title: "About DDoS Protection",
      paragraphs: [
        "DDoS Protection is the always-on layer that watches traffic for the shapes DoS attacks tend to take — rate spikes on a single URI, a rapidly growing number of source IPs, sudden session-rotation patterns, entropy shifts across paths. The main screen shows whether protection is Enabled or Disabled for the current site, four status tiles for the sensitivity and mitigation defaults, and a Recent Activity rail with hourly blocks over the last 24 hours plus the top offending source.",
        "Click Edit configuration to open the settings drawer where every detection vector has its own threshold group: rate-limit (spike multiplier, floor, absolute maximum), IP surge (threshold, growth rate), botnet signatures, URI flood, session rotation, and entropy variance. Defaults are safe for most workloads. Only lower the thresholds when you're seeing an attack pattern your current values miss; only raise them when a legitimate traffic pattern is triggering unwanted mitigation.",
      ],
      learnMoreUrl: `${DOCS_BASE}/ddos-protection`,
    },
  },
  {
    pattern: "/v2/services/firewall_rules",
    slide: {
      title: "About Firewall Rules",
      paragraphs: [
        "Firewall Rules is the site-scoped list of explicit allow, log, challenge, block, or drop policies. Each rule has a name, a set of match conditions (IP, path, method, headers, ASN, country, and more), and an action taken when a request matches. Rules are ordered by priority — the first rule that matches wins.",
        "Add a rule from the primary button; the editor walks through match conditions and the action to take. Draft changes need to be applied — use the Apply button once you're ready for the rules to take effect on live traffic. Deleted rules leave an audit-log entry so you can trace who turned off which policy.",
      ],
      learnMoreUrl: `${DOCS_BASE}/firewall-rules`,
    },
  },

  // -------- API security family (single umbrella slide) -------------
  {
    pattern: "/v2/api-security/*",
    slide: {
      title: "About API Security",
      paragraphs: [
        "API Security is the family of screens that protect the APIs behind your sites — from schema-driven request validation to authentication, DLP, GraphQL guards, posture scoring, and outbound-API inventory. The Overview page rolls up risk factors, 24-hour traffic, schema drift, and top-risk endpoints; the sub-pages let you go deep on each control.",
        "Most controls have three operating modes: Off (feature is dormant), Log-only (feature evaluates traffic and records what it sees but doesn't act), and Enforce (feature acts on its verdict). The standard rollout for a new control is Log-only for at least a week, review the events for surprises, and only then flip to Enforce.",
      ],
      learnMoreUrl: `${DOCS_BASE}/api-security`,
    },
  },

  // -------- settings family (single umbrella slide) -----------------
  {
    pattern: "/v2/settings/*",
    slide: {
      title: "About Settings",
      paragraphs: [
        "The Settings family covers everything that isn't about traffic — the humans, the organisations, the integrations, and the plan. Account is your personal profile plus your API keys. Organisation lets you brand the console and pick a super-user. Project is the site-inventory grouped by project. License shows your plan, feature entitlements, and monthly quotas. SSO is where you enable single sign-on for the whole org. Webhooks / Integrations is where you send logs and alerts elsewhere. Audit Logs is the tamper-evident record of who did what. Alerts is your alert-rule catalogue. Users is user and role management.",
        "Most Settings pages need admin permissions — regular users see a read-only view (or no access) on organisation-level screens. Changes take effect immediately unless a screen explicitly says otherwise.",
      ],
      learnMoreUrl: `${DOCS_BASE}/settings`,
    },
  },

  // -------- reports (currently a stub) ------------------------------
  {
    pattern: "/v2/reports",
    slide: {
      title: "About Reports",
      paragraphs: [
        "Reports is where you'll find scheduled and on-demand rollups of the last N days of security activity — WAF blocks, bot verdicts, DDoS mitigation, top attackers, and account-wide posture score. Reports are packaged for sharing outside the console (PDF or CSV) so you can hand a monthly summary to a stakeholder without giving them console access.",
        "This page is under active development. If it's empty when you visit, it means your account hasn't been enabled yet or scheduled reports haven't been configured. Contact your Layerd account team to be enabled during the rollout.",
      ],
      learnMoreUrl: `${DOCS_BASE}/reports`,
    },
  },
];

// Fallback used whenever the current pathname doesn't match any pattern
// above. The panel still renders — just with a generic pointer at the
// docs site.
const DEFAULT_SLIDE = {
  title: "Layerd documentation",
  paragraphs: [
    "This screen doesn't have a page-specific summary yet. If you're new to the console, the docs site has a walkthrough of every product and how the pages fit together.",
    "You can also hide this panel — click the lightbulb in the top bar. Your preference is remembered per browser.",
  ],
  learnMoreUrl: DOCS_BASE,
};

export { SLIDES_BY_PATH, DEFAULT_SLIDE };
